import type { PlaceLocationResult } from './types';

import { useGoogleMaps } from '~/composables/useGoogleMaps';

/**
 * Fetches coordinates from Google Maps Places API
 * SSR-safe and mockable for testing
 *
 * @returns Function to fetch location by place ID
 */
export function usePlaceLocation() {
	const { init, api } = useGoogleMaps();

	/**
	 * Get latitude and longitude for a Google Place ID
	 * Lazily initializes Google Maps API on first call
	 *
	 * @param placeId - Google Place ID
	 * @returns Object with lat/lng or nulls if unavailable
	 */
	async function getLocation(
		placeId: string | null
	): Promise<PlaceLocationResult> {
		if (!placeId) {
			return { lat: null, lng: null };
		}

		try {
			// Lazily initialize Google Maps when needed
			const googleMapsApi = await init();

			if (!googleMapsApi?.places) {
				return { lat: null, lng: null };
			}

			const { Place } = googleMapsApi.places;

			const place = new Place({
				id: placeId
			});

			await place.fetchFields({
				fields: ['location']
			});

			if (!place.location) {
				return { lat: null, lng: null };
			}
			return {
				lat: place.location.lat(),
				lng: place.location.lng()
			};
		} catch (error) {
			console.error('Error fetching place location:', error);
			return { lat: null, lng: null };
		}
	}

	return {
		getLocation
	};
}
